import React, { useEffect, useState } from "react";
import { Bell, BellOff, Smartphone, Info, Clock } from "lucide-react";
import { toast } from "sonner";
import { updateProfile } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
    enablePush,
    disablePush,
    getCurrentSubscription,
    isPushSupported,
    isStandalone,
    isIOS,
} from "../lib/push";

const REMINDER_CHOICES = [2, 4, 8, 12];

export default function NotificationsCard() {
    const { user, refresh: refreshUser } = useAuth();
    const [state, setState] = useState("loading"); // "loading" | "off" | "on" | "denied" | "unsupported"
    const [busy, setBusy] = useState(false);
    const [savingHours, setSavingHours] = useState(false);
    const currentHours = user?.reminder_hours || 8;

    const setReminderHours = async (h) => {
        if (h === currentHours) return;
        setSavingHours(true);
        try {
            await updateProfile({ reminder_hours: h });
            await refreshUser();
            toast.success(`Streak reminder set to ${h}h before the day ends.`);
        } catch (err) {
            toast.error("Couldn't update the reminder time");
        } finally {
            setSavingHours(false);
        }
    };

    const refresh = async () => {
        if (!isPushSupported()) { setState("unsupported"); return; }
        if (Notification.permission === "denied") { setState("denied"); return; }
        const sub = await getCurrentSubscription();
        setState(sub ? "on" : "off");
    };

    useEffect(() => { refresh(); }, []);

    const handleEnable = async () => {
        setBusy(true);
        try {
            await enablePush();
            toast.success("Notifications enabled. You're on the list.");
            await refresh();
        } catch (err) {
            toast.error(err.message || "Could not enable notifications.");
        } finally {
            setBusy(false);
        }
    };

    const handleDisable = async () => {
        setBusy(true);
        try {
            await disablePush();
            toast.success("Notifications disabled on this device.");
            await refresh();
        } catch (err) {
            toast.error(err.message || "Could not disable notifications.");
        } finally {
            setBusy(false);
        }
    };

    const showIosHint = isIOS() && !isStandalone();

    return (
        <div className="tactical-card" data-testid="notifications-card">
            <div className="flex items-center justify-between">
                <div className="label-tactical">NOTIFICATIONS</div>
                {state === "on" ? <Bell size={16} className="text-[#34C759]" /> : <BellOff size={16} className="text-zinc-500" />}
            </div>

            {showIosHint && (
                <div className="mt-3 border border-[#FF9F0A]/40 bg-[#FF9F0A]/5 p-3 text-xs text-[#FF9F0A]" style={{ borderRadius: 2 }} data-testid="ios-install-hint">
                    <div className="flex items-start gap-2">
                        <Smartphone size={14} className="mt-0.5 flex-shrink-0" />
                        <div>
                            <div className="font-bold uppercase tracking-wider">iOS Install Required</div>
                            <div className="mt-1 text-zinc-300 normal-case font-normal">
                                Tap the <strong>Share</strong> icon in Safari, then <strong>Add to Home Screen</strong>. Open the Nerdy icon from your home screen and come back here to enable notifications.
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="mt-4">
                {state === "loading" && <div className="text-zinc-500 text-sm">Checking…</div>}

                {state === "unsupported" && (
                    <div className="text-zinc-500 text-sm" data-testid="push-unsupported">
                        Your browser doesn't support web push notifications.
                    </div>
                )}

                {state === "denied" && (
                    <div className="text-sm text-zinc-400" data-testid="push-denied">
                        You blocked notifications. Re-enable them in your browser/device settings, then reload.
                    </div>
                )}

                {state === "off" && (
                    <>
                        <p className="text-sm text-zinc-400 mb-3">
                            Get a ping when your streak is about to break, or when a competition is closing.
                        </p>
                        <button
                            onClick={handleEnable}
                            disabled={busy || showIosHint}
                            className="btn-blaze w-full sm:w-auto min-h-[44px] justify-center disabled:opacity-40"
                            data-testid="enable-push-btn"
                        >
                            <Bell size={14} /> {busy ? "ENABLING…" : "ENABLE NOTIFICATIONS"}
                        </button>
                    </>
                )}

                {state === "on" && (
                    <>
                        <p className="text-sm text-[#34C759] mb-3 inline-flex items-center gap-1">
                            <Info size={12} /> This device is subscribed.
                        </p>
                        <div className="mb-4" data-testid="reminder-hours-picker">
                            <div className="label-tactical mb-2 flex items-center gap-1.5">
                                <Clock size={12} /> STREAK REMINDER — BEFORE DAY ENDS
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                {REMINDER_CHOICES.map((h) => (
                                    <button
                                        key={h}
                                        onClick={() => setReminderHours(h)}
                                        disabled={savingHours}
                                        className={`chip ${currentHours === h ? "on" : ""} disabled:opacity-40`}
                                        data-testid={`reminder-hours-${h}`}
                                    >
                                        {h}h
                                    </button>
                                ))}
                            </div>
                            <div className="text-[11px] text-zinc-500 mt-2">
                                You'll get a push {currentHours}h before the streak day resets (UTC midnight) if today's goal isn't done.
                            </div>
                        </div>
                        <button
                            onClick={handleDisable}
                            disabled={busy}
                            className="btn-ghost w-full sm:w-auto min-h-[44px] justify-center disabled:opacity-40"
                            data-testid="disable-push-btn"
                        >
                            <BellOff size={14} /> {busy ? "DISABLING…" : "DISABLE ON THIS DEVICE"}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
